import { Paging } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { CdnConfigInjection } from "features/cdnConfiguration/di/CdnConfigInjection";
import { CdnConfigurationFormRequestEntity } from "features/cdnConfiguration/domain/entities/CdnConfigurationFormRequestEntity";
import { CdnConfigurationListRequestEntity } from "features/cdnConfiguration/domain/entities/CdnConfigurationListRequestEntity";
import { CdnConfigurationResponseEntity } from "features/cdnConfiguration/domain/entities/CdnConfigurationResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";

// ─── Input ────────────────────────────────────────────────────────────────────

export class CdnConfigurationInput implements Disposable {
  public fetchList = new BehaviorSubject<CdnConfigurationListRequestEntity>({
    page: 0,
    limit: 10,
  });
  public uploadFile = new BehaviorSubject<CdnConfigurationFormRequestEntity | null>(null);
  public deleteFile = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.uploadFile.complete();
    this.deleteFile.complete();
  }
}

// ─── Output ───────────────────────────────────────────────────────────────────

export class CdnConfigurationOutput implements Disposable {
  public savedList = new BehaviorSubject<CdnConfigurationResponseEntity[]>([]);
  public pagination = new BehaviorSubject<Paging>({ page: 0, limit: 10, total: 0 });
  public listLoading = new BehaviorSubject<boolean>(false);
  public formLoading = new BehaviorSubject<boolean>(false);
  public onError = new BehaviorSubject<string>("");
  public actionSuccess = new BehaviorSubject<"create" | "update" | "delete" | null>(null);

  [Symbol.dispose](): void {
    this.savedList.complete();
    this.pagination.complete();
    this.listLoading.complete();
    this.formLoading.complete();
    this.onError.complete();
    this.actionSuccess.complete();
  }
}

// ─── ViewModel ────────────────────────────────────────────────────────────────

@injectable()
export class CdnConfigurationViewModel extends BaseViewModel<
  CdnConfigurationInput,
  CdnConfigurationOutput
> {
  constructor() {
    super(new CdnConfigurationInput(), new CdnConfigurationOutput());
  }

  private useCase = CdnConfigInjection.getUseCase();

  protected override transform(): void {
    // Fetch list
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.output.listLoading.next(true);
        const res = await this.useCase.getCdnConfigurationList(params);
        if (res?.data) {
          this.output.savedList.next(res.data);
          this.output.pagination.next(res.pagination || { page: params.page, limit: params.limit });
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.listLoading.next(false);
      }
    });

    // Upload file
    this.skipFirstInitial(this.input.uploadFile, async (entity) => {
      if (!entity) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.uploadCdnConfiguration(entity);
        if (res?.data) {
          this.output.actionSuccess.next("create");
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });

    // Delete file
    this.skipFirstInitial(this.input.deleteFile, async (code) => {
      if (!code) return;
      try {
        this.output.formLoading.next(true);
        const res = await this.useCase.deleteCdnConfiguration({ code });
        if (res) {
          this.output.actionSuccess.next("delete");
        }
      } catch (error: any) {
        this.output.onError.next(
          error?.error?.message || error?.message || strings.GlobalMessage.Error,
        );
      } finally {
        this.output.formLoading.next(false);
      }
    });
  }
}
